import type { IconType } from 'react-icons';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '../../lib/utils';

export type ChoiceTile = {
  value: string;
  label: string;
  /** One short line under the label: "Pay at the front desk". */
  description?: string;
  icon?: IconType;
};

/**
 * One pick from a few options, each a card of its own: an icon, a label and a
 * line saying what the choice means. For a choice worth a moment's thought,
 * like how to pay, where `ChoiceList`'s plain rows read as a form to get
 * through.
 *
 * Drawn like the package rows it sits beside: a white card at rest, the brand
 * ring and wash once picked, and the icon chip filling in with the brand green.
 *
 * Each tile is a Radix radio, so the arrow keys move between them and a screen
 * reader hears a radio group. Takes what `Field` hands a control, so it binds
 * the same way: `<Field name="paymentMethod" label="…"><ChoiceTiles … /></Field>`.
 */
export function ChoiceTiles({
  id,
  value,
  options,
  onChange,
  onBlur,
  className,
  ref,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  id?: string;
  value?: unknown;
  options: readonly ChoiceTile[];
  onChange?: (next: string) => void;
  onBlur?: () => void;
  className?: string;
  /** Lands on the first tile, so a form can focus the group when it is wrong on submit. */
  ref?: React.Ref<HTMLButtonElement>;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  // Only leaving the whole group counts as the field being touched.
  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onBlur?.();
  };

  return (
    <RadioGroupPrimitive.Root
      id={id}
      value={typeof value === 'string' ? value : ''}
      onValueChange={(next) => onChange?.(next)}
      onBlur={handleBlur}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      className={cn('grid grid-cols-2 gap-3', className)}
    >
      {options.map(({ value: optionValue, label, description, icon: Icon }, i) => (
        <RadioGroupPrimitive.Item
          key={optionValue}
          ref={i === 0 ? ref : undefined}
          value={optionValue}
          className={cn(
            'group flex items-center gap-3 rounded-card bg-surface p-3.5 text-left text-ink shadow-card',
            'transition-colors duration-(--duration-fast) ease-standard motion-reduce:transition-none',
            'hover:bg-raised',
            // The same inset ring as a picked package row, so it never spills into the gap.
            'data-[state=checked]:bg-brand-wash data-[state=checked]:ring-2 data-[state=checked]:ring-brand-edge data-[state=checked]:ring-inset',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            ariaInvalid && 'ring-1 ring-destructive ring-inset',
          )}
        >
          {Icon && (
            <span
              aria-hidden="true"
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-wash text-brand-text',
                'transition-colors duration-(--duration-fast) ease-standard motion-reduce:transition-none',
                'group-data-[state=checked]:bg-brand group-data-[state=checked]:text-on-brand',
              )}
            >
              <Icon className="size-4.5" />
            </span>
          )}

          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-base font-medium">{label}</span>
            {description && <span className="text-xs text-ink-muted">{description}</span>}
          </span>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
